import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { SaveBeforeRejectConfirmationModal } from '../../../components/SaveBeforeRejectConfirmationModal';
import { useLoader } from '../../../helpers/useLoader';
import { useAnnotationPermissions } from '../../annotation/annotation.permissions';
import { IAnnotationsStore } from '../../annotation/annotations.store';
import { ReviewNavigation } from '../../annotation/components/ReviewNavigation';
import { IReviewRejectReasonsStore } from '../../annotation/submodules/reviewRejectReasones/reviewRejectReasons.store';
import { IFreeAccessService } from '../freeAccess.service';
import { IFreeAccessStore } from '../freeAccess.store';

export const FreeAccessReviewControlsContainer = observer((): ReactElement => {
  const freeAccessService = useInjection(IFreeAccessService.$);
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const reviewRejectReasonsStore = useInjection(IReviewRejectReasonsStore.$);
  const { isLoading, withLoaderAsync } = useLoader();
  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [annotationConfirmationModalMode, setAnnotationConfirmationModalMode] = useState(AnnotationConfirmationModalMode.ACCEPT);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showSaveBeforeRejectConfirmationModal, setShowSaveBeforeRejectConfirmationModal] = useState(false);
  const permissions = useAnnotationPermissions();

  const handleAccept = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => freeAccessService.acceptAnnotationAsync(), 'accept-button');
  };

  const handleReject = async (): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.REJECT);
    return Promise.resolve();
  };

  const handleConfirmReject = async (reason?: string): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    if (reason) {
      await handleRejectConfirmed(reason);
    } else {
      setShowSaveBeforeRejectConfirmationModal(true);
    }
  };

  const handleRejectConfirmed = async (reason?: string): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    freeAccessStore.rejectionReason = reason;
    await withLoaderAsync(async () => freeAccessService.rejectAnnotationAsync(reason), 'reject-button');
  };

  const handleSaveBeforeRejectConfirmed = async (): Promise<void> => {
    await handleRejectConfirmed();
  };

  const handleRejectCanceled = (): void => {
    setShowSaveBeforeRejectConfirmationModal(false);
    setShowAnnotationConfirmationModal(false);
  };

  const handleEdit = async (): Promise<void> => {
    await freeAccessService.changeModeToEdit();
  };

  const toggleAnnotationConfirmationModal = (mode?: AnnotationConfirmationModalMode): void => {
    if (mode !== undefined) {
      setAnnotationConfirmationModalMode(mode);
    }
    setShowAnnotationConfirmationModal((prevState) => !prevState);
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => freeAccessService.discardAnnotationAsync(EditorMode.FREEACCESS_REVIEW), 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  const handleCancel = async (): Promise<void> => {
    await withLoaderAsync(async () => freeAccessService.cancelReviewAnnotationAsync(), 'cancel-annotation-button');
  };

  const { uiStore } = freeAccessService;
  const disabled = isLoading || uiStore.isImageLoading || annotationsStore.image === undefined;

  const handleAcceptHotkey = (): void => {
    setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.ACCEPT);
    toggleAnnotationConfirmationModal();
  };

  const handleRejectHotkey = (): void => {
    setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.REJECT);
    toggleAnnotationConfirmationModal();
  };

  useCustomHotkeys('ctrl+shift+enter', handleAcceptHotkey, { enabled: !disabled }, [handleAcceptHotkey]);
  useCustomHotkeys('ctrl+shift+backspace', handleRejectHotkey, { enabled: !disabled }, [handleRejectHotkey]);
  useCustomHotkeys(['e', 'E'], handleEdit, { enabled: !disabled }, [handleEdit]);

  return (
    <>
      <ReviewNavigation
        disabled={disabled}
        canDiscard={permissions.canDiscard().allowed}
        editorMode={EditorMode.FREEACCESS_REVIEW}
        onAccept={handleAccept}
        onReject={handleReject}
        onEdit={handleEdit}
        canCancel={true}
        onCancel={handleCancel}
        showDiscardModal={showDiscardConfirmationModal}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
      />
      <SaveBeforeRejectConfirmationModal
        onSaveBeforeRejectCancel={handleRejectCanceled}
        onSaveBeforeRejectConfirm={handleSaveBeforeRejectConfirmed}
        onSaveBeforeRejectDecline={handleReject}
        showSaveBeforeRejectModal={showSaveBeforeRejectConfirmationModal}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={annotationConfirmationModalMode}
        rejectAutoCompleteOptions={reviewRejectReasonsStore.rejectionReasons}
        onAnnotationConfirm={annotationConfirmationModalMode === AnnotationConfirmationModalMode.ACCEPT ? handleAccept : handleConfirmReject}
        onAnnotationCancel={toggleAnnotationConfirmationModal}
      />
    </>
  );
});
